import { ReactElement, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Helmet } from "react-helmet";
import styled from 'styled-components';
import { isNull } from 'lodash';
import { withLDProvider, useLDClient, useFlags, basicLogger } from 'launchdarkly-react-client-sdk';

import useAuthStore from "models/auth/auth.store";
import useUserStore from "models/user/user.store";
import { FEATURE_FLAGS_CONTEXT, buildFeatureFlagsContext } from 'config/featureFlags';
import * as RefreshToken from 'utils/refresh-token';
import Login from 'pages/login/login';
import HeaderAndMenuLayout from 'containers/headerAndMenuLayout/HeaderAndMenuLayout';
import { useFetchLoggedUser } from 'models/user/user.queries';
import { getAppContainerStyles } from './App.styles';
import { ThemeProps } from 'types/theme';
import { useLogoImg } from 'utils/images';
import CONST from 'utils/constants';
import { useFetchDynamicMenu, useFetchPrivateApps } from 'models/workflows/workflows.queries';
import { getFirstMenuItemUrl } from 'utils/workflows';
import useWorkflowsStore from 'models/workflows/workflows.store';
import WorkflowsPrivateView from 'pages/workflowsAppView';
import { PrivateCustomFlow } from 'pages/formHost';
import EmptyStatePage from 'pages/emptyState/emptyState';
import LoadingErrorPage from 'pages/loadingError/loadingError';
import { PageLoading } from 'components/loading';
import { IdleTimer } from './IdleTimer';

// 2mins
const IDLE_TIMEOUT = 1000 * 60 * 2;

const AppContainer = styled.div`
  ${({ theme }: ThemeProps): string => getAppContainerStyles(theme)}
`
const logo = useLogoImg(CONST.ASSET_NAMES.LOGO_SQUARE);
function App(): ReactElement {
  const ldClient = useLDClient();
  const [ldReady, setLdReady] = useState(false);
  // Whether `identify()` has resolved the LD context for the real user. The
  // dynamic menu must not be fetched before this, otherwise the flag is still
  // evaluated against the anonymous context and the wrong endpoint is chosen.
  const [ldIdentified, setLdIdentified] = useState(false);
  const { isFetching: isFetchingLoggedUser, refetch: refetchLoggedUser } = useFetchLoggedUser();
  const { isSignedIn, logout } = useAuthStore();
  const { profile } = useUserStore();
  const userId = (profile as { id?: string })?.id;
  // Reactive flag value, evaluated against the identified context (see below).
  const useCustomDynamicMenu = Boolean(useFlags()['use-custom-dynamic-menu']);
  // Fetch the menu only once the user is identified in LD, so the flag (and
  // therefore the endpoint) is already correct and we never fetch twice.
  const { isPending: isPendingFetchDynamicMenu, isError: isErrorFetchingDynamicMenu } = useFetchDynamicMenu(useCustomDynamicMenu, { enabled: isSignedIn && ldReady && ldIdentified });
  const { isPending: isPendingFetchPrivateApps, isError: isErrorFetchingPrivateApps } = useFetchPrivateApps({ enabled: isSignedIn });
  const { dynamicMenu, privateApps } = useWorkflowsStore()
  const [firstUrl, setFirstUrl] = useState("")
  const isLoading = (isSignedIn && (isPendingFetchDynamicMenu || isPendingFetchPrivateApps)) || isFetchingLoggedUser;
  const isError = isSignedIn && (isErrorFetchingDynamicMenu || isErrorFetchingPrivateApps)

  // Wait for the LD client to be ready. `finally` guarantees we never hang the
  // app if LD fails to initialise.
  useEffect(() => {
    if (!ldClient) return;
    ldClient.waitForInitialization().catch(() => undefined).finally(() => setLdReady(true));
  }, [ldClient])

  // Reset the identified flag on logout: `logout()` clears the stores but this
  // local state would otherwise survive, letting a relogin (different user) in
  // the same session fetch the menu against the previous LD context.
  useEffect(() => {
    if (!isSignedIn) setLdIdentified(false);
  }, [isSignedIn])

  // The logged-user query runs once on mount (401 before login). Refetch it
  // once signed in (and only while the profile is still missing) so it loads on
  // first login too — not only after a browser reload — which triggers
  // `identify()` below.
  useEffect(() => {
    if (isSignedIn && !userId) {
      void refetchLoggedUser();
    }
  }, [isSignedIn, userId, refetchLoggedUser])

  // Enrich the LD context with the authenticated user once the profile loads,
  // and mark LD as identified so the menu can be fetched with the right flag.
  useEffect(() => {
    if (!ldClient || !ldReady || !userId) return;
    ldClient
      .identify(buildFeatureFlagsContext(userId))
      .catch(() => undefined)
      .finally(() => setLdIdentified(true));
  }, [ldClient, ldReady, userId])

  // Start refresh token cycle after login or refreshing the browser.
  useEffect(() => {
    if (isSignedIn) {
      RefreshToken.initRefresh();
    }
  }, [isSignedIn])

  useEffect(() => {
    if (!isLoading && !isNull(dynamicMenu) && !isNull(privateApps)) {
      setFirstUrl(getFirstMenuItemUrl(dynamicMenu));
    }
  }, [isLoading, dynamicMenu, privateApps])

  const onIdle = (): void => {
    if (isSignedIn) {
      logout();
    }
  }
  
  return (
    <IdleTimer timeout={IDLE_TIMEOUT} onIdle={onIdle}>
      <Helmet> 
        <title>{process.env.CUSTOMER_LONG_NAME}</title> {/* <-- El título dinámico */}
        <link rel="shortcut icon" href={logo} />
      </Helmet>
      <AppContainer>
        {isLoading ?
          <PageLoading/>
        : <BrowserRouter>
            <Routes>
              {/* Redirect */}
              {isError && <Route path="/" element={<Navigate to="/loading-error" />} />}
              {isError && <Route path="/work/*" element={<Navigate to="/loading-error" />} />}
              {isError && <Route path="/flow/*" element={<Navigate to="/loading-error" />} />}
              {/* The below line makes sure that when reloading a page after an error it goes to the first url if everything goes fine */}
              {isSignedIn && !isError && <Route path="/loading-error" element={<Navigate to={firstUrl} />} />}
              {isSignedIn && !isError && <Route path="/" element={<Navigate to={firstUrl} />} />}
              
              {/* Public Route */}
              <Route path="/" element={<Login />} />

              {/* Private Routes */}
              <Route
                path="/work/:key"
                element={isSignedIn ?
                  <HeaderAndMenuLayout>
                    <WorkflowsPrivateView />
                  </HeaderAndMenuLayout>
                  : <Navigate to="/" replace />}
              />
              <Route
                path="/flow/:flowId/*"
                element={isSignedIn ?
                  <HeaderAndMenuLayout>
                    <PrivateCustomFlow />
                  </HeaderAndMenuLayout>
                  : <Navigate to="/" replace />}
              />
              <Route
                path="/no-apps-installed"
                element={isSignedIn ?
                  <HeaderAndMenuLayout>
                    <EmptyStatePage />
                  </HeaderAndMenuLayout>
                   : <Navigate to="/" replace />}
              />
              <Route 
                path="/loading-error" 
                element={isSignedIn ?
                  <HeaderAndMenuLayout>
                    <LoadingErrorPage />
                  </HeaderAndMenuLayout>
                   : <Navigate to="/" replace />}
              />
              <Route path="*" element={<div>NOT FOUND</div>} />
            </Routes>
          </BrowserRouter>
        }
      </AppContainer>
    </IdleTimer>
  )
};

export default withLDProvider({
  clientSideID: process.env.FEATURE_FLAG_CLIENT_ID || '',
  context: FEATURE_FLAGS_CONTEXT,
  // Keep the original kebab-case flag keys in `useFlags()` so they match the
  // literal keys used with `variation()` (e.g. 'use-custom-dynamic-menu').
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  options: {
    logger: basicLogger({ level: 'none' }),
  },
})(App);
